import { caseDataGaps, caseGapsNote, type CaseDto, type CaseListItemDto, datePl, type Page, type PossibleDuplicateCaseDto, type ProcessCandidateDto, todayInWarsaw } from '@bqcrm/shared';
import { PossibleDuplicatesAlert } from '../../components/PossibleDuplicates';
import { Alert, Button, Group, Modal, Select, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconAlertTriangle, IconCopy, IconHandGrab, IconPlayerPause, IconPlayerPlay, IconRocket, IconX } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api';
import { useInvalidateProcess } from '../../components/process/ProcessTasks';
import { useCaseMutation } from '../../data';
import { CaseHoldDialog, CaseUnholdDialog } from './HoldDialogs';
import { formatDateTime } from '../../format';
import { notifyOk } from '../../notify';

type Dialog = 'approve' | 'missing' | 'reject' | 'duplicate' | 'unhold' | 'extend' | 'contact' | null;

/**
 * QA6 P16: link „Ustaw zastępstwo” z paska wstrzymania — nowa nieobecność opiekuna w Kalendarzu (z osobą zastępującą), okres wstrzymania podstawiony.
 * QA7 P2: bez powodu z wstrzymania (zastępstwo obejmuje wszystkie sprawy opiekuna) — numer sprawy tylko jako informacja w oknie.
 */
function substitutionLink(c: CaseDto): string {
  const p = new URLSearchParams({ nowa: 'nieobecnosc', od: todayInWarsaw() });
  if (c.hold?.until) p.set('do', c.hold.until);
  if (c.owner) p.set('kto', c.owner.id);
  p.set('sprawa', c.number);
  return `/kalendarz?${p.toString()}`;
}

function useIntake(c: CaseDto, path: string, done: () => void) {
  const refresh = useInvalidateProcess();
  return useCaseMutation(c.id, (body: unknown) => api<CaseDto>(`/cases/${c.id}/${path}`, { method: 'POST', body }), () => {
    refresh(c.id);
    done();
  });
}

/** Braki danych przed nadaniem biegu (QA P17) — ostrzeżenie, bez blokady; po nadaniu biegu dane uzupełnia się w kartach klienta i lokalizacji. */
function DataGapsAlert({ c, mb }: { c: CaseDto; mb?: string }) {
  const gaps = caseDataGaps({ emails: c.client.emails, street: c.site.street, postalCode: c.site.postalCode });
  if (!gaps.length) return null;
  return (
    <Alert mb={mb} variant="light" color="yellow" icon={<IconAlertTriangle size={18} />} title="Niepełne dane">
      <Text size="sm">
        {gaps.join('; ')}. Możesz nadać bieg — uzupełnij dane w karcie klienta / lokalizacji, gdy będą znane.
      </Text>
    </Alert>
  );
}

function ApproveModal({ c, onClose }: { c: CaseDto; onClose: () => void }) {
  const people = useQuery({ queryKey: ['process-candidates-type', 'oferta'], queryFn: () => api<ProcessCandidateDto[]>('/process/candidates?typ=oferta') });
  const [sales, setSales] = useState<string | null>('kolejka');
  const [comment, setComment] = useState('');
  const m = useIntake(c, 'intake/approve', onClose);
  return (
    <Modal opened onClose={onClose} title="Nadanie biegu sprawie">
      <Stack>
        <Text size="sm" c="dimmed">
          Sprawa wejdzie w obieg: powstanie zadanie przygotowania oferty dla każdej firmy w sprawie.
        </Text>
        <DataGapsAlert c={c} />
        <Select
          label="Kto przygotuje ofertę"
          data={[{ value: 'kolejka', label: 'Kolejka — przejmie pierwszy uprawniony handlowiec' }, ...(people.data ?? []).map((p) => ({ value: p.id, label: p.fullName }))]}
          value={sales}
          onChange={setSales}
          allowDeselect={false}
          searchable
        />
        <Textarea label="Komentarz" placeholder="np. klient prosi o kontakt po 16:00" autosize minRows={2} value={comment} onChange={(e) => setComment(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button leftSection={<IconRocket size={16} />} loading={m.isPending} onClick={() => m.mutate({ salesUserId: sales === 'kolejka' ? null : sales, comment })}>
            Nadaj bieg
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function TextModal({ c, title, label, path, field, okLabel, color, onClose, placeholder, extra, initial }: { c: CaseDto; title: string; label: string; path: string; field: string; okLabel: string; color?: string; onClose: () => void; placeholder?: string; extra?: React.ReactNode; initial?: string }) {
  const [text, setText] = useState(initial ?? '');
  const [until, setUntil] = useState('');
  const m = useIntake(c, path, onClose);
  return (
    <Modal opened onClose={onClose} title={title}>
      <Stack>
        <Textarea label={label} placeholder={placeholder} autosize minRows={2} value={text} onChange={(e) => setText(e.currentTarget.value)} data-autofocus />
        {extra}
        {path === 'hold' && <TextInput type="date" label="Wznowić dnia (opcjonalnie)" description="Sprawa wróci sama tego dnia" value={until} onChange={(e) => setUntil(e.currentTarget.value)} />}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button color={color} loading={m.isPending} disabled={text.trim().length < 3} onClick={() => m.mutate(path === 'hold' ? { reason: text, until: until || null } : { [field]: text })}>
            {okLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Przedłużenie wstrzymania — nowa data wznowienia (QA U2). */
function ExtendHoldModal({ c, onClose }: { c: CaseDto; onClose: () => void }) {
  const [until, setUntil] = useState(c.hold?.until ?? '');
  const [reason, setReason] = useState('');
  const m = useIntake(c, 'hold/extend', onClose);
  return (
    <Modal opened onClose={onClose} title="Przedłużenie wstrzymania">
      <Stack>
        <TextInput type="date" label="Wznowić dnia" description="Sprawa wróci sama tego dnia; dzień wcześniej opiekun dostanie zadanie przypomnienia" value={until} min={todayInWarsaw()} onChange={(e) => setUntil(e.currentTarget.value)} data-autofocus />
        <TextInput label="Powód (opcjonalnie)" placeholder="np. klient wraca później" value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button loading={m.isPending} disabled={!until || until < todayInWarsaw()} onClick={() => m.mutate({ until, reason })}>
            Przedłuż
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** QA6 P24: próba kontaktu z klientem (sprawa do uzupełnienia) — wpis w historii, kolejna próba następnego dnia roboczego. */
function ContactAttemptModal({ c, onClose }: { c: CaseDto; onClose: () => void }) {
  const [note, setNote] = useState('');
  const m = useIntake(c, 'intake/contact-attempt', () => (notifyOk('Zapisano próbę kontaktu'), onClose()));
  const phone = c.client.phones[0] || c.onsiteContact?.phone || null;
  return (
    <Modal opened onClose={onClose} title="Próba kontaktu z klientem">
      <Stack>
        <Text size="sm">
          Brakuje: {c.intake.missingNote}
          {phone ? ` · tel. ${phone}` : ''}
        </Text>
        <Textarea label="Wynik (opcjonalnie)" placeholder="np. nie odbiera, prośba o SMS, oddzwoni jutro" autosize minRows={2} value={note} onChange={(e) => setNote(e.currentTarget.value)} data-autofocus />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button loading={m.isPending} onClick={() => m.mutate({ note })}>
            Zapisz próbę kontaktu
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function DuplicateModal({ c, onClose, preset }: { c: CaseDto; onClose: () => void; preset?: PossibleDuplicateCaseDto }) {
  const [q, setQ] = useState('');
  const [dq] = useDebouncedValue(q, 300);
  const found = useQuery({ queryKey: ['cases', 'dup', dq], queryFn: () => api<Page<CaseListItemDto>>(`/cases?limit=20&offset=0&q=${encodeURIComponent(dq)}`), enabled: dq.length >= 2 });
  const [orig, setOrig] = useState<string | null>(preset?.id ?? null);
  const [reason, setReason] = useState('');
  const m = useIntake(c, 'intake/duplicate', onClose);
  return (
    <Modal opened onClose={onClose} title="Oznaczenie duplikatu">
      <Stack>
        <Select
          label="Sprawa, której to duplikat"
          placeholder="numer, nazwisko, adres…"
          searchable
          searchValue={q}
          onSearchChange={setQ}
          filter={({ options }) => options}
          data={[...(preset ? [{ value: preset.id, label: `${preset.number} · ${preset.clientName} · ${preset.title}` }] : []), ...(found.data?.items ?? []).filter((x) => x.id !== c.id && x.id !== preset?.id).map((x) => ({ value: x.id, label: `${x.number} · ${x.clientName} · ${x.title}` }))]}
          value={orig}
          onChange={setOrig}
          nothingFoundMessage={dq.length >= 2 ? 'Nie znaleziono' : 'Wpisz co najmniej 2 znaki'}
        />
        <TextInput label="Uwagi" value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button color="red" disabled={!orig} loading={m.isPending} onClick={() => m.mutate({ duplicateOfId: orig, reason })}>
            Zamknij jako duplikat
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Przyjęcie sprawy (docs/10, Etap 1): do przejęcia → weryfikacja wstępna → nadanie biegu albo odrzucenie / duplikat;
 * oraz wstrzymanie całej sprawy (flaga z powodem i datą powrotu).
 */
export function IntakePanel({ c }: { c: CaseDto }) {
  const [dialog, setDialog] = useState<Dialog>(null);
  const [dupOf, setDupOf] = useState<string | null>(null);
  const close = () => setDialog(null);
  const claim = useIntake(c, 'intake/claim', () => notifyOk('Przejęto sprawę'));
  const completed = useIntake(c, 'intake/completed', () => notifyOk('Sprawa wróciła do weryfikacji'));
  const i = c.intake;
  const pending = !c.closedAt && ['nowa', 'weryfikacja', 'do_uzupelnienia'].includes(i.status);
  const decide = i.can.decide || (i.status === 'nowa' && i.can.claim);

  return (
    <>
      {pending && (
        <Alert
          mb="md"
          variant="light"
          color={i.status === 'nowa' ? 'orange' : i.status === 'do_uzupelnienia' ? 'yellow' : 'blue'}
          icon={i.status === 'do_uzupelnienia' ? <IconAlertTriangle size={18} /> : <IconHandGrab size={18} />}
          title={i.status === 'nowa' ? 'Nowa sprawa — czeka na opiekuna' : i.status === 'do_uzupelnienia' ? 'Wymaga uzupełnienia' : 'Weryfikacja wstępna'}
        >
          <Stack gap="xs">
            <Text size="sm">
              {i.status === 'nowa'
                ? 'Sprawa nie jest jeszcze w obiegu. Opiekun przejmuje ją, sprawdza dane i nadaje bieg — dopiero wtedy powstaje zadanie przygotowania oferty.'
                : i.status === 'do_uzupelnienia'
                  ? `Brakuje: ${i.missingNote}`
                  : `Opiekun ${c.owner?.fullName ?? '—'} sprawdza dane klienta, lokalizację i zakres${i.claimedAt ? ` (przejęta ${formatDateTime(i.claimedAt)})` : ''}. Po weryfikacji nadaj bieg — wskaż handlowca albo skieruj ofertę do kolejki.`}
            </Text>
            {i.status === 'do_uzupelnienia' && i.contactAttempts.length > 0 && (
              <Text size="xs" c="dimmed">
                Próby kontaktu ({i.contactAttempts.length}):{' '}
                {i.contactAttempts.map((a) => `${formatDateTime(a.at)}${a.byName ? ` ${a.byName}` : ''}${a.note ? ` — ${a.note}` : ''}`).join(' · ')}
              </Text>
            )}
            {(i.can.claim || decide || i.can.contact) && (
              <Group gap="xs">
                {i.status === 'do_uzupelnienia' && i.can.contact && (
                  <Button size="xs" variant="light" color="yellow" onClick={() => setDialog('contact')}>
                    Zapisz próbę kontaktu
                  </Button>
                )}
                {i.status === 'nowa' && i.can.claim && (
                  <Button size="xs" leftSection={<IconHandGrab size={14} />} loading={claim.isPending} onClick={() => claim.mutate(undefined)}>
                    Przejmij sprawę
                  </Button>
                )}
                {i.status !== 'nowa' && i.can.decide && (
                  <Button size="xs" leftSection={<IconRocket size={14} />} onClick={() => setDialog('approve')}>
                    Nadaj bieg
                  </Button>
                )}
                {i.status === 'weryfikacja' && i.can.decide && (
                  <Button size="xs" variant="light" color="yellow" onClick={() => setDialog('missing')}>
                    Do uzupełnienia
                  </Button>
                )}
                {i.status === 'do_uzupelnienia' && i.can.decide && (
                  <Button size="xs" variant="light" loading={completed.isPending} onClick={() => completed.mutate(undefined)}>
                    Uzupełniono — wróć do weryfikacji
                  </Button>
                )}
                {decide && (
                  <>
                    <Button size="xs" variant="subtle" color="red" leftSection={<IconX size={14} />} onClick={() => setDialog('reject')}>
                      Odrzuć zapytanie…
                    </Button>
                    <Button size="xs" variant="subtle" color="gray" leftSection={<IconCopy size={14} />} onClick={() => setDialog('duplicate')}>
                      Oznacz jako duplikat…
                    </Button>
                  </>
                )}
              </Group>
            )}
          </Stack>
        </Alert>
      )}
      {/* QA P20: braki danych (adres bez ulicy / kodu, brak e-maila) widoczne od założenia zapytania */}
      {pending && i.status !== 'do_uzupelnienia' && <DataGapsAlert c={c} mb="md" />}
      {/* P8: otwarte sprawy tego samego klienta lub adresu w tej samej branży */}
      {pending && (
        <PossibleDuplicatesAlert
          items={i.possibleDuplicates}
          onDuplicate={
            decide
              ? (id) => {
                  setDupOf(id);
                  setDialog('duplicate');
                }
              : undefined
          }
        />
      )}
      {c.hold && (
        <Alert mb="md" variant="light" color="gray" icon={<IconPlayerPause size={18} />} title={`Sprawa wstrzymana${c.hold.until ? ` do ${datePl(c.hold.until)}` : ''}`}>
          <Group justify="space-between" gap="xs">
            <Text size="sm">
              {c.hold.reason}
              <Text span size="xs" c="dimmed">
                {' '}
                · {c.hold.byName ?? '—'}, {formatDateTime(c.hold.at)}
              </Text>
            </Text>
            {c.can.hold && (
              <Group gap="xs">
                {/* QA6 P16: zastępstwo za nieobecnego opiekuna sprawy (nie za zalogowaną osobę), powód z wstrzymania */}
                <Button size="xs" variant="subtle" color="gray" component={Link} to={substitutionLink(c)}>
                  Ustaw zastępstwo
                </Button>
                <Button size="xs" variant="subtle" onClick={() => setDialog('extend')}>
                  Przedłuż…
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconPlayerPlay size={14} />}
                  // okno: powód przed ustaloną datą wznowienia (QA D12), powiadomienie klienta (P34)
                  onClick={() => setDialog('unhold')}
                >
                  Wznów sprawę
                </Button>
              </Group>
            )}
          </Group>
        </Alert>
      )}
      {dialog === 'approve' && <ApproveModal c={c} onClose={close} />}
      {dialog === 'missing' && <TextModal c={c} title="Skierowanie do uzupełnienia" label="Czego brakuje i kto ma uzupełnić" path="intake/missing" field="note" okLabel="Do uzupełnienia" onClose={close} placeholder="np. brak metrażu, zdjęć miejsca montażu" initial={caseGapsNote({ emails: c.client.emails, street: c.site.street, postalCode: c.site.postalCode })} />}
      {dialog === 'reject' && <TextModal c={c} title="Odrzucenie zapytania" label="Przyczyna odrzucenia" path="intake/reject" field="reason" okLabel="Odrzuć zapytanie" color="red" onClose={close} placeholder="np. poza obszarem działania, spam" />}
      {dialog === 'duplicate' && <DuplicateModal c={c} onClose={() => (close(), setDupOf(null))} preset={i.possibleDuplicates.find((d) => d.id === dupOf)} />}
      {dialog === 'extend' && <ExtendHoldModal c={c} onClose={close} />}
      {dialog === 'contact' && <ContactAttemptModal c={c} onClose={close} />}
      {dialog === 'unhold' && <CaseUnholdDialog c={c} onClose={close} />}
    </>
  );
}

export function HoldModal({ c, onClose }: { c: CaseDto; onClose: () => void }) {
  return <CaseHoldDialog c={c} onClose={onClose} />;
}
